import React, { useEffect, useRef } from 'react';
import './ContextMenu.css';

interface NodeContextMenuProps {
  position: { x: number; y: number };
  nodeId: string;
  nodeName: string;
  revealActive: boolean;
  onReveal: (nodeId: string) => void;
  onClose: () => void;
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  position,
  nodeId,
  nodeName,
  revealActive,
  onReveal,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  useEffect(() => {
    if (menuRef.current) {
      const firstButton = menuRef.current.querySelector('button');
      if (firstButton) firstButton.focus();
    }
  }, []);

  const actionLabel = revealActive ? `Expand around ${nodeName}` : `Focus around ${nodeName}`;

  return (
    <div
      ref={menuRef}
      className="context-menu"
      role="menu"
      aria-label="Node context menu"
      tabIndex={0}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        background: '#fff',
        border: '1px solid #ccc',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 10,
        listStyle: 'none',
        padding: 0,
        margin: 0,
      }}
      aria-modal="true"
    >
      <div className="context-menu-title">Node actions</div>
      <ul>
        <li style={{ padding: 0, margin: 0, border: 'none', background: 'none' }}>
          <button
            className="context-menu-item"
            type="button"
            role="menuitem"
            tabIndex={0}
            onClick={() => onReveal(nodeId)}
          >
            {actionLabel}
          </button>
        </li>
      </ul>
    </div>
  );
};

export default NodeContextMenu;
